import {
  COMPLEXITY_FEE_LABEL,
  DISABLE_PRICE_MODIFIERS_FLAG,
  LOCAL_ANESTHESIA_ELIGIBLE_FLAG,
  MODIFIER_CAP_PCT,
  NEGOTIABLE_PROCEDURE_FRACTION,
  PHOTO_UPLOAD_BONUS_FRACTION,
  PRICING_CONFIG,
  SPECIAL_FLAG,
} from "./pricingConfig";

export type PricingProcedureInput = {
  // Procedure catalog id (Procedure._id). Used by callers to look up
  // procedure-specific things (modifier scope, etc.).
  id: string;
  // QuoteInvoiceItem._id for the row that introduced this procedure into
  // the invoice. Discounts reference this so that a single invoice line
  // can be targeted (vs. all rows that share a catalog id).
  invoiceItemId?: string;
  name: string;
  standardPrice: number;
  isLocalAnesthesiaEligible: boolean;
  // Limited-time-offer flag. When true, `standardPrice` is the exact
  // billed price for this line — no $ modifier, no % uplift, no
  // negotiable-fraction discount. See SPECIAL_FLAG in pricingConfig.
  isSpecial?: boolean;
  // Suppresses ALL percentage risk-factor modifiers (auto + manual/
  // custom) for this procedure's line ONLY — `percentageModifiers` below
  // is ignored and the line is charged at exactly standardPrice + the
  // sales-rep $ modifier. Independent of isSpecial. See
  // DISABLE_PRICE_MODIFIERS_FLAG in pricingConfig.
  disablePriceModifiers?: boolean;
  // Dollar adjustment from the sales rep (e.g. +500, -200). Defaults to 0.
  modifier?: number;
  // Risk-factor percentage modifiers (additive). Each entry is a
  // percentage value, e.g. 20 = +20%. Summed across all entries, capped
  // at MODIFIER_CAP_PCT, then applied multiplicatively to the
  // (standardPrice + modifier) base.
  percentageModifiers?: number[];
};

export type PricingAddonInput = {
  // Addon catalog id.
  id: string;
  // QuoteInvoiceItem._id for the row that introduced this addon into
  // the invoice. Discounts reference this.
  invoiceItemId?: string;
  name: string;
  price: number;
  modifier?: number;
};

export type PricingDiscountTarget = "PROCEDURE" | "ADDON" | "TOTAL";
export type PricingDiscountKind = "PERCENTAGE" | "FLAT";

// Discount line attached to a quote. Targets either a specific
// procedure/addon line (by id) or the whole invoice (target === "TOTAL").
// PERCENTAGE values are expressed as 0-100 (25 = 25% off); FLAT values
// are dollar amounts. Stacking is additive — multiple discounts on the
// same target sum their effects before being applied.
export type PricingDiscountInput = {
  id: string;
  target: PricingDiscountTarget;
  targetItemId?: string | null;
  kind: PricingDiscountKind;
  value: number;
  reason?: string | null;
};

export type PricingLineItemKind =
  | "ANESTHESIA"
  | "ASSISTANTS"
  | "MATERIALS"
  | "OR_COSTS"
  | "SURGEON_FEE"
  | "PROCEDURE"
  // Booking-calendar fixed-price line (ALL_IN mode): the snapshot price
  // IS the line amount — no OR block, no surgeon fee, no negotiation
  // fraction. Risk modifiers surface as ONE trailing MODIFIER row over
  // the fixed-line subtotal instead of inflating each line.
  | "TIER_PRICING"
  | "ADDON"
  // Flat-dollar surcharge (lump-sum modifier — e.g. "Increased
  // Complexity"). Added once to the quote total at face value; never
  // percentage-scaled, never modifier-uplifted.
  | "MODIFIER"
  | "DISCOUNT";

export type PricingMode = "STANDARD" | "ALL_IN" | "V2";

export type PricingLineItem = {
  kind: PricingLineItemKind;
  description: string;
  // `amount` is the modified amount (what the patient/invoice ultimately
  // owes for this line). For non-PROCEDURE kinds, baseAmount === amount.
  // For DISCOUNT lines, amount is negative.
  amount: number;
  baseAmount: number;
  procedureId?: string;
  addonId?: string;
  // QuoteInvoiceItem._id for the row backing this line item. Populated
  // for PROCEDURE/ADDON kinds so DISCOUNT line items can look up the
  // targeted line by invoice-row id (vs. catalog id).
  invoiceItemId?: string;
  // Set on DISCOUNT line items so callers can render them next to or
  // beneath the targeted line.
  discountId?: string;
  discountTargetItemId?: string | null;
  discountTarget?: PricingDiscountTarget;
  // Reason attached to the discount (audit trail for FLAT; optional
  // otherwise). Surfaced so display layers can render "Save $X — <reason>".
  discountReason?: string | null;
};

export type PatientDisplayRow = {
  procedureId: string;
  name: string;
  amount: number;          // base price shown prominently
  amountWithModifiers: number; // upper-bound shown as "Up to $X"
  // True for the first procedure row — its amount bundles the OR-cost
  // block and the first surgeon-fee tier. UI uses this to render a
  // "includes facility fees" hint.
  includesBaseCosts: boolean;
};

export type QuotePricing = {
  lineItems: PricingLineItem[];
  baseBlockTotal: number;
  surgeonFeeTotal: number;
  proceduresTotal: number;      // base (no risk modifiers)
  proceduresTotalWithModifiers: number;
  // After modifier uplift, then × NEGOTIABLE_PROCEDURE_FRACTION. This is
  // the "lowest price" the sales rep can negotiate procedure costs to;
  // base OR + surgeon fees are NOT discounted.
  proceduresTotalLowest: number;
  addonsTotal: number;
  // Sum of all discount adjustments (positive number; subtracted from
  // totalWithModifiers to produce totalAfterDiscounts).
  discountsTotal: number;
  total: number;                // base total — modifier-free reference
  totalWithModifiers: number;   // upper-bound — what's actually owed without negotiation OR discounts
  // What the patient actually owes after applying every Discount line
  // attached to the quote. This is the headline number shown on the
  // estimate PDF + the planner total row.
  totalAfterDiscounts: number;
  totalLowest: number;          // attractive "today's price" shown to patients
  localAnesthesiaApplied: boolean;
  firstSurgeonFee: number;
  additionalSurgeonFee: number;
  // Fraction actually applied to procedure prices for the `totalLowest`
  // calculation (e.g. 0.75 without photos, 0.50 with). Surfaced so
  // patient-display callers can compute matching per-row amounts.
  effectiveFraction: number;
};

const parseModifier = (modifier: number | undefined): number =>
  typeof modifier === "number" && Number.isFinite(modifier) ? modifier : 0;

// Sums percentage modifiers, caps at MODIFIER_CAP_PCT, returns the
// effective uplift as a unitless multiplier-delta (e.g. 30 → 0.30).
const upliftFor = (pcts: number[] | undefined): number => {
  if (!pcts || pcts.length === 0) return 0;
  const sum = pcts.reduce((s, p) => s + (Number.isFinite(p) ? p : 0), 0);
  const capped = Math.min(Math.max(sum, 0), MODIFIER_CAP_PCT);
  return capped / 100;
};

// Per-procedure uplift: a DISABLE_PRICE_MODIFIERS procedure takes NO
// percentage uplift (its line is charged at face value); everyone else
// gets the capped sum of their % modifiers. Every uplift computation in
// this file MUST go through this helper so a future call site can't
// silently skip the exemption.
const upliftForProcedure = (p: PricingProcedureInput): number =>
  p.disablePriceModifiers ? 0 : upliftFor(p.percentageModifiers);

export type QuotePricingInput = {
  procedures: PricingProcedureInput[];
  addons?: PricingAddonInput[];
  // Discount line items attached to the quote. Applied AFTER procedures,
  // addons, OR-block and surgeon fee are summed. Line-scoped discounts
  // (target === PROCEDURE | ADDON) reduce the targeted line's
  // modifier-applied amount; TOTAL-scoped discounts reduce the overall
  // subtotal. Multiple discounts on the same target stack additively.
  discounts?: PricingDiscountInput[];
  // True when the patient has uploaded at least one photo — unlocks an
  // additional PHOTO_UPLOAD_BONUS_FRACTION discount on the procedure
  // portion of `totalLowest`.
  hasUploadedPhotos?: boolean;
  // Flat-dollar surcharges (lump-sum modifiers). Each becomes its own
  // MODIFIER line item and is added once to the quote total at FACE
  // VALUE — never percentage-uplifted, never negotiated/scaled. `amount`
  // is whole dollars.
  flatModifiers?: { label: string; amount: number }[];
  // ALL_IN = booking-calendar mode (07_INTEGRATION_V2 § 4.1): fixed-price
  // TIER_PRICING lines, no OR block / surgeon fees / negotiation
  // fraction; risk modifiers become one trailing surcharge row over the
  // fixed-line subtotal. Default STANDARD — bit-identical to the
  // pre-mode behavior (the ALL_IN branch returns before the standard
  // body runs).
  // "V2" = ESTIMATE_NEW_PRICING estimate mode — STANDARD body with
  // effectiveFraction pinned to 1 (see above).
  pricingMode?: PricingMode;
};

// Discounts apply on top of the modifier-applied totals. Two phases:
//   1. Line-scoped discounts (PROCEDURE/ADDON) reduce the targeted
//      line's modifier-applied amount. Stacking is additive — if the
//      sum of percentages for one target exceeds 100, the discount is
//      clamped to the line amount (no negative final amounts).
//   2. TOTAL-scoped discounts reduce the subtotal AFTER line-scoped
//      discounts have been deducted.
//
// Each discount produces a DISCOUNT line item with a negative amount so
// renderers (estimate PDF, planner) can show them inline. Mutates
// `lineItems` (appends DISCOUNT rows) and returns the discounts total.
// `procedureLineKind` is "PROCEDURE" in STANDARD mode and "TIER_PRICING"
// in ALL_IN mode — PROCEDURE-target discounts deduct from whichever kind
// carries the quote's procedure lines.
function applyDiscountPhase(
  lineItems: PricingLineItem[],
  discounts: PricingDiscountInput[],
  totalWithModifiers: number,
  procedureLineKind: "PROCEDURE" | "TIER_PRICING",
): number {
  let discountsTotal = 0;
  // Index by QuoteInvoiceItem._id (invoiceItemId) so discounts can
  // target the specific invoice row that introduced a procedure/addon,
  // not all rows that share a catalog id.
  const procedureAmountByItemId = new Map<string, number>();
  const addonAmountByItemId = new Map<string, number>();
  for (const li of lineItems) {
    if (li.kind === procedureLineKind && li.invoiceItemId) {
      procedureAmountByItemId.set(li.invoiceItemId, li.amount);
    } else if (li.kind === "ADDON" && li.invoiceItemId) {
      addonAmountByItemId.set(li.invoiceItemId, li.amount);
    }
  }

  const applyDiscount = (discount: PricingDiscountInput, base: number) => {
    const reduction =
      discount.kind === "PERCENTAGE"
        ? Math.round(base * (Math.max(0, discount.value) / 100))
        : Math.max(0, Math.round(discount.value));
    const clamped = Math.min(reduction, base);
    if (clamped <= 0) return 0;
    // Standalone discount lines read "Adjustment — <reason>" (owner decision
    // 2026-08-09) — never the rate: the first procedure row rolls facility
    // costs into its patient-facing amount, so a "50% off" label reads as
    // wrong against the rolled-up number the patient sees (owner decision
    // 2026-07-28).
    const description = `Adjustment${discount.reason ? ` — ${discount.reason}` : ""}`;
    lineItems.push({
      kind: "DISCOUNT",
      description,
      amount: -clamped,
      baseAmount: -clamped,
      discountId: discount.id,
      discountTargetItemId: discount.targetItemId ?? null,
      discountTarget: discount.target,
      discountReason: discount.reason ?? null,
    });
    discountsTotal += clamped;
    return clamped;
  };

  // Group line-scoped discounts by their target item so we can deduct
  // from the same base in order, clamping to avoid negative line totals.
  const lineDiscounts = discounts.filter(
    (d) => d.target !== "TOTAL" && d.targetItemId,
  );
  const totalDiscounts = discounts.filter((d) => d.target === "TOTAL");

  const remainingByProcedure = new Map<string, number>();
  const remainingByAddon = new Map<string, number>();
  for (const [k, v] of procedureAmountByItemId) remainingByProcedure.set(k, v);
  for (const [k, v] of addonAmountByItemId) remainingByAddon.set(k, v);

  for (const d of lineDiscounts) {
    const id = d.targetItemId!;
    if (d.target === "PROCEDURE") {
      const remaining = remainingByProcedure.get(id);
      if (remaining == null || remaining <= 0) continue;
      const used = applyDiscount(d, remaining);
      remainingByProcedure.set(id, remaining - used);
    } else if (d.target === "ADDON") {
      const remaining = remainingByAddon.get(id);
      if (remaining == null || remaining <= 0) continue;
      const used = applyDiscount(d, remaining);
      remainingByAddon.set(id, remaining - used);
    }
  }

  // Subtotal after line-scoped discounts is the base for TOTAL-scoped.
  let runningTotal = totalWithModifiers - discountsTotal;
  for (const d of totalDiscounts) {
    if (runningTotal <= 0) break;
    const used = applyDiscount(d, runningTotal);
    runningTotal -= used;
  }

  return discountsTotal;
}

// ALL_IN pricing (booking-calendar, 07_INTEGRATION_V2 § 4.1). The total a
// patient owes = Σ fixed lines × (1 + capped risk modifiers) − discounts:
//
//   - Each procedure is a TIER_PRICING line at its snapshot price — the
//     line amounts are what the patient was shown, never inflated.
//   - Risk modifiers (already capped per-line by upliftFor) surface as
//     ONE trailing MODIFIER row over the fixed-line subtotal, mirroring
//     how the patient-facing flow itemizes them.
//   - No OR-cost block, no surgeon-fee tiering, no negotiation fraction:
//     totalLowest === totalWithModifiers and effectiveFraction is 1.
//   - Discounts (e.g. the photo-sharing credit) subtract after modifiers
//     via the same machinery as STANDARD, targeting TIER_PRICING lines.
function calculateAllInPricing(input: QuotePricingInput): QuotePricing {
  const procedures = input.procedures ?? [];
  const addons = input.addons ?? [];
  const discounts = input.discounts ?? [];
  const flatModifiers = input.flatModifiers ?? [];

  const lineItems: PricingLineItem[] = [];

  let proceduresTotal = 0;
  let riskSurchargeExact = 0;
  let uniformUplift: number | null | undefined = undefined;
  for (const p of procedures) {
    const baseAmount = (p.standardPrice ?? 0) + parseModifier(p.modifier);
    const uplift = upliftForProcedure(p);
    proceduresTotal += baseAmount;
    riskSurchargeExact += baseAmount * uplift;
    // Track whether every line carries the same uplift (the normal case —
    // risk factors are patient-level) so the surcharge row can show the
    // rate; mixed per-line scopes drop the rate from the label.
    uniformUplift =
      uniformUplift === undefined || uniformUplift === uplift ? uplift : null;
    lineItems.push({
      kind: "TIER_PRICING",
      description: p.name,
      amount: baseAmount,
      baseAmount,
      procedureId: p.id,
      invoiceItemId: p.invoiceItemId,
    });
  }

  // One trailing risk-surcharge row over the whole fixed-line subtotal —
  // a single rounding, so the row exactly equals subtotal × rate.
  const riskSurcharge = Math.round(riskSurchargeExact);
  if (riskSurcharge > 0) {
    const rateLabel =
      uniformUplift != null && uniformUplift > 0
        ? ` (+${Math.round(uniformUplift * 100)}%)`
        : "";
    lineItems.push({
      kind: "MODIFIER",
      description: `${COMPLEXITY_FEE_LABEL}${rateLabel}`,
      amount: riskSurcharge,
      baseAmount: riskSurcharge,
    });
  }

  // Addons + lump-sum modifiers pass through at face value (not part of
  // the patient flow, but coordinators may add them post-booking).
  let addonsTotal = 0;
  for (const a of addons) {
    const amount = (a.price ?? 0) + parseModifier(a.modifier);
    addonsTotal += amount;
    lineItems.push({
      kind: "ADDON",
      description: `Addon: ${a.name}`,
      amount,
      baseAmount: amount,
      addonId: a.id,
      invoiceItemId: a.invoiceItemId,
    });
  }
  let flatModifiersTotal = 0;
  for (const fm of flatModifiers) {
    const amount = parseModifier(fm.amount);
    if (amount === 0) continue;
    flatModifiersTotal += amount;
    lineItems.push({
      kind: "MODIFIER",
      description: fm.label,
      amount,
      baseAmount: amount,
    });
  }

  const total = proceduresTotal + addonsTotal;
  const proceduresTotalWithModifiers = proceduresTotal + riskSurcharge;
  const totalWithModifiers =
    proceduresTotalWithModifiers + addonsTotal + flatModifiersTotal;

  const discountsTotal = applyDiscountPhase(
    lineItems,
    discounts,
    totalWithModifiers,
    "TIER_PRICING",
  );

  const totalAfterDiscounts = Math.max(0, totalWithModifiers - discountsTotal);

  return {
    lineItems,
    baseBlockTotal: 0,
    surgeonFeeTotal: 0,
    proceduresTotal,
    proceduresTotalWithModifiers,
    // No negotiation fraction in ALL_IN — "lowest" IS the modifier total.
    proceduresTotalLowest: proceduresTotalWithModifiers,
    addonsTotal,
    discountsTotal,
    total,
    totalWithModifiers,
    totalAfterDiscounts,
    totalLowest: totalWithModifiers,
    localAnesthesiaApplied: false,
    firstSurgeonFee: 0,
    additionalSurgeonFee: 0,
    effectiveFraction: 1,
  };
}

export function calculateQuotePricing(input: QuotePricingInput): QuotePricing {
  if (input.pricingMode === "ALL_IN") {
    return calculateAllInPricing(input);
  }
  const procedures = input.procedures ?? [];
  const addons = input.addons ?? [];
  const discounts = input.discounts ?? [];
  const flatModifiers = input.flatModifiers ?? [];
  const hasUploadedPhotos = input.hasUploadedPhotos ?? false;
  // V2 (ESTIMATE_NEW_PRICING): the negotiation fractions are RETIRED —
  // the authored per-procedure minimumPrice (enforced upstream in the
  // assembly via clampNegotiatedPrice) replaces the derived floor, so the
  // fraction is pinned to 1 and hasUploadedPhotos is ignored. Everything
  // else (4-bucket structure, SPECIAL/COMBO semantics, modifier cap,
  // discounts) intentionally reuses this STANDARD body unchanged.
  const effectiveFraction =
    input.pricingMode === "V2"
      ? 1
      : hasUploadedPhotos
        ? NEGOTIABLE_PROCEDURE_FRACTION - PHOTO_UPLOAD_BONUS_FRACTION
        : NEGOTIABLE_PROCEDURE_FRACTION;

  const lineItems: PricingLineItem[] = [];

  // Specials sidestep the OR/surgeon overhead and the negotiable-fraction
  // discount — and they do so for the ENTIRE quote, not just their own
  // line. If any procedure in the quote is a special, the facility block +
  // surgeon fee are dropped wholesale. The special's all-in price absorbs
  // the overhead even when mixed with non-specials. Specials still take the
  // risk-factor % uplift (see the per-procedure loop below). See
  // SPECIAL_FLAG in pricingConfig.ts.
  const nonSpecialProcedures = procedures.filter((p) => !p.isSpecial);
  const hasAnySpecial = procedures.some((p) => p.isSpecial);
  const includeFacility = !hasAnySpecial && nonSpecialProcedures.length > 0;

  // Local-anesthesia exemption: every procedure in the quote must be
  // flagged. (Reachable only when includeFacility is already true, so
  // there are no specials in the mix.)
  const localAnesthesiaApplied =
    includeFacility &&
    nonSpecialProcedures.every((p) => p.isLocalAnesthesiaEligible);

  const pushFlat = (
    kind: PricingLineItemKind,
    description: string,
    amount: number,
  ) => lineItems.push({ kind, description, amount, baseAmount: amount });

  // OR-cost block — only when no special is present in the quote.
  if (includeFacility) {
    if (!localAnesthesiaApplied) {
      pushFlat("ANESTHESIA", "Anesthesia", PRICING_CONFIG.baseORCosts.anesthesia);
    }
    pushFlat("ASSISTANTS", "Assistants", PRICING_CONFIG.baseORCosts.assistants);
    pushFlat("MATERIALS", "Materials", PRICING_CONFIG.baseORCosts.materials);
    pushFlat("OR_COSTS", "Other OR costs", PRICING_CONFIG.baseORCosts.otherOrCosts);
  }

  const baseBlockTotal = lineItems.reduce((s, li) => s + li.amount, 0);

  // Surgeon fee: $1000 first + $500 each additional. Counted over all
  // procedures when no special is present; suppressed entirely when
  // any special is in the mix (same all-in semantics as the facility
  // block above).
  const firstSurgeonFee = includeFacility
    ? PRICING_CONFIG.surgeonFee.firstProcedure
    : 0;
  const additionalSurgeonFee = PRICING_CONFIG.surgeonFee.additionalProcedure;
  const surgeonFeeTotal = includeFacility
    ? firstSurgeonFee + additionalSurgeonFee * (nonSpecialProcedures.length - 1)
    : 0;

  if (surgeonFeeTotal > 0) {
    pushFlat(
      "SURGEON_FEE",
      nonSpecialProcedures.length === 1
        ? "Surgeon fee"
        : `Surgeon fee (${nonSpecialProcedures.length} procedures)`,
      surgeonFeeTotal,
    );
  }

  // Per-procedure standard prices + sales-rep $ modifier + risk-factor
  // % uplift. Base = price + modifier; modified = base * (1 + uplift).
  // The "lowest" share per procedure is rounded here and summed so the
  // patient-display rows literally add up to `totalLowest` (no off-by-
  // pennies between line-item sum and headline).
  let proceduresTotal = 0;
  let proceduresTotalWithModifiers = 0;
  let proceduresTotalLowestAccum = 0;
  for (const p of procedures) {
    if (p.isSpecial) {
      // Specials keep their all-in promo price — no OR/surgeon overhead,
      // no sales-rep $ modifier, no negotiable-fraction discount — but they
      // DO take the risk-factor % uplift, so a higher-risk patient pays more
      // than the advertised special. `baseAmount` is the modifier-free
      // reference; `amount` carries the uplift. With no discount applied,
      // the "lowest" share equals `amount` (a special never drops below its
      // advertised price), so summing the rows still lands on totalLowest.
      const baseAmount = p.standardPrice ?? 0;
      const uplift = upliftForProcedure(p);
      const amount = Math.round(baseAmount * (1 + uplift));
      proceduresTotal += baseAmount;
      proceduresTotalWithModifiers += amount;
      proceduresTotalLowestAccum += amount;
      lineItems.push({
        kind: "PROCEDURE",
        description: p.name,
        amount,
        baseAmount,
        procedureId: p.id,
        invoiceItemId: p.invoiceItemId,
      });
      continue;
    }
    const baseAmount = (p.standardPrice ?? 0) + parseModifier(p.modifier);
    const uplift = upliftForProcedure(p);
    const amount = Math.round(baseAmount * (1 + uplift));
    const lowestAmount = Math.round(amount * effectiveFraction);
    proceduresTotal += baseAmount;
    proceduresTotalWithModifiers += amount;
    proceduresTotalLowestAccum += lowestAmount;
    lineItems.push({
      kind: "PROCEDURE",
      description: p.name,
      amount,
      baseAmount,
      procedureId: p.id,
      invoiceItemId: p.invoiceItemId,
    });
  }

  // Addons pass through as their own line items; they don't affect the
  // OR block or surgeon fee, and risk-factor modifiers don't apply.
  let addonsTotal = 0;
  for (const a of addons) {
    const amount = (a.price ?? 0) + parseModifier(a.modifier);
    addonsTotal += amount;
    lineItems.push({
      kind: "ADDON",
      description: `Addon: ${a.name}`,
      amount,
      baseAmount: amount,
      addonId: a.id,
      invoiceItemId: a.invoiceItemId,
    });
  }

  // Flat-dollar (lump-sum) modifiers — one MODIFIER line item each, added
  // to the quote total at face value. They are intentionally excluded
  // from `total` (the modifier-free reference) just as the % uplift is,
  // and they're never scaled by the negotiable fraction, so they appear
  // unchanged in `totalLowest` too.
  let flatModifiersTotal = 0;
  for (const fm of flatModifiers) {
    const amount = parseModifier(fm.amount);
    if (amount === 0) continue;
    flatModifiersTotal += amount;
    lineItems.push({
      kind: "MODIFIER",
      description: fm.label,
      amount,
      baseAmount: amount,
    });
  }

  const total =
    baseBlockTotal + surgeonFeeTotal + proceduresTotal + addonsTotal;
  const totalWithModifiers =
    baseBlockTotal +
    surgeonFeeTotal +
    proceduresTotalWithModifiers +
    addonsTotal +
    flatModifiersTotal;
  const proceduresTotalLowest = proceduresTotalLowestAccum;
  const totalLowest =
    baseBlockTotal +
    surgeonFeeTotal +
    proceduresTotalLowest +
    addonsTotal +
    flatModifiersTotal;

  const discountsTotal = applyDiscountPhase(
    lineItems,
    discounts,
    totalWithModifiers,
    "PROCEDURE",
  );

  const totalAfterDiscounts = Math.max(0, totalWithModifiers - discountsTotal);

  return {
    lineItems,
    baseBlockTotal,
    surgeonFeeTotal,
    proceduresTotal,
    proceduresTotalWithModifiers,
    proceduresTotalLowest,
    addonsTotal,
    discountsTotal,
    total,
    totalWithModifiers,
    totalAfterDiscounts,
    totalLowest,
    localAnesthesiaApplied,
    firstSurgeonFee,
    additionalSurgeonFee,
    effectiveFraction,
  };
}

// Repackages the calculator output for the patient-facing summary:
// the first non-special procedure absorbs the OR-cost block + first
// surgeon-fee tier so the patient sees one "all-in" number; subsequent
// non-special procedures show their procedure price + the additional
// surgeon-fee tier only. Specials never absorb overhead and are never
// discounted, but they DO carry the risk-factor uplift, so a special's
// row shows its uplifted price (today's price === "up to").
//
// The procedure portion of each non-special row is scaled by
// `effectiveFraction` (the same one used to compute `totalLowest`) so
// summing all row amounts equals `totalLowest`. The "Up to" companion
// uses the full modifier-applied procedure cost — summing those
// equals `totalWithModifiers`. OR-cost block and surgeon fees are
// flat (never discounted), so they appear at face value in their rows.
export function groupPricingForPatientDisplay(
  procedures: PricingProcedureInput[],
  pricing: QuotePricing,
): PatientDisplayRow[] {
  const firstNonSpecialIdx = procedures.findIndex((p) => !p.isSpecial);
  // `additionalSurgeonFee` is non-zero in the QuotePricing struct even when
  // no surgeon fee is actually charged (e.g. a special is in the mix →
  // surgeonFeeTotal is 0). Gate it so the per-row breakdown still sums to
  // totalLowest / totalWithModifiers — mirrors the same guard in
  // estimateLineItems.ts and Quote.generateInvoicePDF.
  const additionalSurgeonFee =
    pricing.surgeonFeeTotal > 0 ? pricing.additionalSurgeonFee : 0;
  return procedures.map((p, idx) => {
    if (p.isSpecial) {
      // All-in promo price with the risk-factor uplift applied, no
      // negotiable-fraction discount — so today's price === "up to".
      const baseAmount = p.standardPrice ?? 0;
      const uplift = upliftForProcedure(p);
      const amount = Math.round(baseAmount * (1 + uplift));
      return {
        procedureId: p.id,
        name: p.name,
        amount,
        amountWithModifiers: amount,
        includesBaseCosts: false,
      };
    }
    const baseProcedure = (p.standardPrice ?? 0) + parseModifier(p.modifier);
    const uplift = upliftForProcedure(p);
    const modifiedProcedure = Math.round(baseProcedure * (1 + uplift));
    const lowestProcedure = Math.round(
      modifiedProcedure * pricing.effectiveFraction,
    );
    if (idx === firstNonSpecialIdx) {
      return {
        procedureId: p.id,
        name: p.name,
        amount:
          lowestProcedure + pricing.baseBlockTotal + pricing.firstSurgeonFee,
        amountWithModifiers:
          modifiedProcedure + pricing.baseBlockTotal + pricing.firstSurgeonFee,
        includesBaseCosts: true,
      };
    }
    return {
      procedureId: p.id,
      name: p.name,
      amount: lowestProcedure + additionalSurgeonFee,
      amountWithModifiers: modifiedProcedure + additionalSurgeonFee,
      includesBaseCosts: false,
    };
  });
}

export {
  DISABLE_PRICE_MODIFIERS_FLAG,
  LOCAL_ANESTHESIA_ELIGIBLE_FLAG,
  MODIFIER_CAP_PCT,
  NEGOTIABLE_PROCEDURE_FRACTION,
  PHOTO_UPLOAD_BONUS_FRACTION,
  PRICING_CONFIG,
  SPECIAL_FLAG,
};

// Medical-history snapshot used to derive AUTO pricing modifiers. Both
// the server (when stamping the modifiers[] subdocument on a Quote) and
// the client (for the patient-facing "Up to $X" preview) compute from
// the same shape — kept colocated with the calculator to guarantee
// they stay aligned.
export type MedicalHistorySnapshot = {
  age?: number | string | null;
  weight?: number | string | null;
  heightFt?: number | string | null;
  heightIn?: number | string | null;
  hasMedicalConditions?: boolean | null;
  // When provided (booking-calendar flow): drives the per-medication
  // ladder from MODIFIER_RULES.perMedication INSTEAD of the
  // hasMedicalConditions boolean. The two are mutually exclusive.
  // Absent/null/negative/NaN → falls back to hasMedicalConditions.
  medicationCount?: number | null;
  lost50pounds?: boolean | null;
  priorCosmeticSurgery?: boolean | null;
  smoker?: boolean | null;
};

export const tierPctFor = (
  tiers: { min: number; max: number; pct: number }[],
  value: number,
): number => {
  const t = tiers.find((t) => value >= t.min && value < t.max);
  return t ? t.pct : 0;
};

const computeBmi = (
  weight: number | string | null | undefined,
  heightFt: number | string | null | undefined,
  heightIn: number | string | null | undefined,
): number | null => {
  const w = Number(weight);
  const ft = Number(heightFt);
  const inches = Number(heightIn) || 0;
  if (!w || !ft) return null;
  const totalInches = ft * 12 + inches;
  if (totalInches <= 0) return null;
  return (w / (totalInches * totalInches)) * 703;
};

// Aggregates every AUTO modifier from a medical-history snapshot into a
// single percentage uplift (already capped). Use this when you only
// need the number for display — e.g. the patient-facing summary's
// "Up to $X" subtext during the quote funnel before the Quote has been
// saved to the DB.
//
// Medication logic — two mutually exclusive paths:
//   • medicationCount provided (finite number ≥ 0): per-med ladder from
//     MODIFIER_RULES.perMedication (+pctPerMedication per med, capped at
//     capPct). A count of 0 produces 0% even when hasMedicalConditions
//     is true — the count replaces the boolean entirely.
//   • medicationCount absent/null/negative/NaN: falls back to the legacy
//     hasMedicalConditions flat +medicalConditions.yes%. Standard quote
//     funnel always takes this path (it passes no medicationCount), so
//     its output is bit-identical to before this change.
export function computeAutoModifierPercentage(
  history: MedicalHistorySnapshot,
): number {
  let sum = 0;

  const age = Number(history.age);
  if (Number.isFinite(age) && age > 0) {
    sum += tierPctFor(PRICING_CONFIG_RULES.age, age);
  }

  const bmi = computeBmi(history.weight, history.heightFt, history.heightIn);
  if (bmi != null) {
    sum += tierPctFor(PRICING_CONFIG_RULES.bmi, bmi);
  }

  // Medication modifier — per-med ladder when count is available,
  // flat boolean otherwise. The two paths are mutually exclusive.
  const rawCount = history.medicationCount;
  const countIsValid =
    rawCount != null && Number.isFinite(rawCount) && rawCount >= 0;
  if (countIsValid) {
    const { pctPerMedication, capPct } = PRICING_CONFIG_RULES.perMedication;
    sum += Math.min(Math.floor(rawCount as number) * pctPerMedication, capPct);
  } else {
    if (history.hasMedicalConditions)
      sum += PRICING_CONFIG_RULES.medicalConditions.yes;
  }

  if (history.lost50pounds) sum += PRICING_CONFIG_RULES.weightLossHx.yes;
  if (history.priorCosmeticSurgery) sum += PRICING_CONFIG_RULES.priorSurgery.yes;
  if (history.smoker) sum += PRICING_CONFIG_RULES.smoker.yes;

  return Math.min(Math.max(sum, 0), MODIFIER_CAP_PCT);
}

// Re-exported under a stable alias so the function above can reach the
// rules without a circular type-only import.
import { MODIFIER_RULES as PRICING_CONFIG_RULES } from "./pricingConfig";
export { MODIFIER_RULES } from "./pricingConfig";
